import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Ban, CalendarClock, Clock, Play, Plus, Search, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { decodeFilters, decodeSort, encodeFilters, encodeSort, purchaseOrderListColumns, purchaseOrderStatuses } from "@patronix/domain/list";
import type { ListTools } from "@/components/ListToolbar";
import { DataList, DetailPanel, DetailPlaceholder, type Column, type ListView } from "@/components/DataList";
import type { MassTarget } from "@/components/MassPanel";
import { Badge } from "@/components/ui";
import {
  isOpenStatus,
  isStatusFilter,
  listPurchaseOrders,
  purchaseOrderDetailQuery,
  purchaseOrderListQuery,
  statusTone,
  type PurchaseOrderRow,
  type StatusFilter,
} from "@/features/purchase-orders/api";
import { PurchaseOrderPanel } from "@/features/purchase-orders/PurchaseOrderPanel";
import { PurchaseOrderMassPanel } from "@/features/purchase-orders/PurchaseOrderMass";
import type { PurchaseOrderMassKind } from "@/features/purchase-orders/mass";
import { EXPORT_PAGE, type ExportColumn } from "@/lib/exportXlsx";
import { ImportPanel } from "@/features/import/ImportPanel";
import { purchaseOrderKeys } from "@/features/purchase-orders/api";
import { activeCompanyOf, useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { todayIso, useFormat } from "@/lib/format";
import { sessionContextQuery, useCan } from "@/lib/session";

// Purchase orders: list → detail. List = one keyset page per request through
// purchase_order_page (status filter + search on number prefix / supplier);
// detail = purchase_order_document (header + lines, one request), prefetched
// on row hover. Writes through the PO RPCs only.

interface Search {
  status: StatusFilter;
  q: string;
  po?: string | undefined;
  /** Open the order as a new copy (toolbar Duplicate). */
  copy?: true | undefined;
  /** The import panel is open (replaces the order detail). */
  import?: true | undefined;
  /** Column filters (encodeFilters) and sort ("-amount"). */
  f?: string | undefined;
  s?: string | undefined;
}

function listParams(companyId: string, s: Search) {
  return {
    companyId,
    status: s.status,
    q: s.q,
    filters: decodeFilters(purchaseOrderListColumns, s.f),
    sort: decodeSort(purchaseOrderListColumns, s.s),
  };
}

export const Route = createFileRoute("/_app/procurement/purchase-orders")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const search: Search = {
      status: isStatusFilter(raw.status) ? raw.status : "open",
      q: typeof raw.q === "string" ? raw.q.slice(0, 100) : "",
    };
    if (typeof raw.po === "string" && raw.po) search.po = raw.po;
    if (search.po && search.po !== "new" && (raw.copy === true || raw.copy === "true")) search.copy = true;
    if (raw.import === true || raw.import === "true") search.import = true;
    const f = encodeFilters(decodeFilters(purchaseOrderListColumns, raw.f));
    if (f) search.f = f;
    const sort = encodeSort(decodeSort(purchaseOrderListColumns, raw.s));
    if (sort) search.s = sort;
    return search;
  },
  loaderDeps: ({ search }) => ({ status: search.status, q: search.q, f: search.f, s: search.s }),
  // Start the first page while the route chunk is still loading.
  loader: async ({ context, deps }) => {
    const ctx = await context.queryClient.ensureQueryData(sessionContextQuery);
    const companyId = activeCompanyOf(ctx)?.id;
    if (companyId) await context.queryClient.ensureInfiniteQueryData(purchaseOrderListQuery(listParams(companyId, deps as Search)));
  },
  component: PurchaseOrdersPage,
});

const filters: StatusFilter[] = ["open", "draft", "released", "partially_received", "received", "all"];

function PurchaseOrdersPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const canManage = useCan("procurement.po.manage");
  const canImport = useCan("core.data.import") && canManage;
  const { company } = useActiveCompany();
  // Mass operation open in the detail area (checked rows or the whole filter;
  // docs/design/mass-operations.md).
  const [mass, setMass] = useState<{ kind: PurchaseOrderMassKind; target: MassTarget; count: number | null } | null>(null);
  const exportColumns: ExportColumn<PurchaseOrderRow>[] = [
    { header: t("po.colOrder"), kind: "code", value: (r) => r.number },
    { header: t("export.supplierCode"), kind: "code", value: (r) => r.supplier_code },
    { header: t("po.colSupplier"), kind: "text", value: (r) => r.supplier_name },
    { header: t("po.colStatus"), kind: "code", value: (r) => r.status },
    { header: t("po.orderDate"), kind: "date", value: (r) => r.order_date },
    { header: t("po.colDelivery"), kind: "date", value: (r) => r.next_promised_date },
    { header: t("po.colAmount"), kind: "number", value: (r) => r.total_amount, unit: (r) => r.currency },
    { header: t("po.currency"), kind: "code", value: (r) => r.currency },
    { header: t("export.lines"), kind: "integer", value: (r) => r.line_count },
  ];

  const setSearch = useCallback(
    (patch: Partial<Search>) =>
      void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );

  const listTools: ListTools<PurchaseOrderRow> = {
    id: "purchase-orders",
    title: t("po.title"),
    onNew: canManage
      ? () => {
          setMass(null);
          setSearch({ po: "new", copy: undefined, import: undefined });
        }
      : undefined,
    onDuplicate: canManage
      ? (r) => {
          setMass(null);
          setSearch({ po: r.id, copy: true, import: undefined });
        }
      : undefined,
    export: { fileBase: "purchase-orders", sheet: t("po.title"), columns: exportColumns, fetchPage: (cursor, signal) =>
              listPurchaseOrders(listParams(company?.id ?? "", search), cursor, signal, EXPORT_PAGE) },
    onImport: canImport
      ? () => {
          setMass(null);
          setSearch({ import: true, po: undefined });
        }
      : undefined,
    mass:
      canManage && company
        ? (
            [
              { key: "release", label: t("mass.purchaseOrders.release"), icon: <Play size={15} /> },
              { key: "move", label: t("mass.purchaseOrders.move"), icon: <CalendarClock size={15} /> },
              { key: "cancel", label: t("mass.purchaseOrders.cancel"), icon: <Ban size={15} /> },
            ] as const
          ).map((a) => ({
            ...a,
            open: (target: MassTarget, count: number | null) => {
              setSearch({ po: undefined, copy: undefined, import: undefined });
              setMass({ kind: a.key, target, count });
            },
          }))
        : [],
  };

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.procurement.title")} <span aria-hidden="true">/</span> {t("nav.procurement.purchaseOrders")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("po.title")}</h1>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => {
              setMass(null);
              setSearch({ po: "new", import: undefined });
            }}
            className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-accent pl-3 pr-4 font-medium text-on-accent hover:bg-accent-hover"
          >
            <Plus size={18} strokeWidth={2} aria-hidden="true" />
            {t("po.newOrder")}
          </button>
        )}
      </header>

      <Toolbar search={search} setSearch={setSearch} />

      <div className="flex min-h-0 grow gap-4">
        <OrderList search={search} setSearch={setSearch} tools={listTools} onPick={() => setMass(null)} />
        <DetailPanel
          label={mass ? t(`mass.purchaseOrders.${mass.kind}Title`) : search.import ? t("import.title") : t("po.detailLabel")}
          open={!!mass || !!search.po || !!search.import}
          placeholder={<DetailPlaceholder title={t("po.selectPrompt")} hint={t("po.selectPromptHint")} />}
        >
          {mass ? (
            <PurchaseOrderMassPanel
              key={`${mass.kind}:${mass.target.kind}:${mass.count ?? "all"}`}
              kind={mass.kind}
              target={mass.target}
              count={mass.count}
              list={listParams(company?.id ?? "", search)}
              onClose={() => setMass(null)}
            />
          ) : search.import ? (
            <ImportPanel
              entities={["purchase_orders"]}
              initial="purchase_orders"
              companyId={company?.id}
              onClose={() => setSearch({ import: undefined })}
              invalidate={[purchaseOrderKeys.all]}
            />
          ) : search.po && (
            <PurchaseOrderPanel
              key={`${search.po}:${search.copy ? "copy" : ""}`}
              poId={search.po}
              startAsCopy={!!search.copy}
              onClose={() => setSearch({ po: undefined, copy: undefined })}
              onOpen={(id) => setSearch({ po: id, copy: undefined })}
            />
          )}
        </DetailPanel>
      </div>
    </div>
  );
}

function Toolbar({ search, setSearch }: { search: Search; setSearch: (p: Partial<Search>) => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState(search.q);

  // Debounce typing into the URL (and therefore the query key) by 200 ms.
  // Superseded requests are cancelled through the query's AbortSignal.
  useEffect(() => {
    if (text === search.q) return;
    const id = setTimeout(() => setSearch({ q: text, po: undefined }), 200);
    return () => clearTimeout(id);
  }, [text, search.q, setSearch]);

  const label = (f: StatusFilter) =>
    f === "open" ? t("po.filterOpen") : f === "all" ? t("po.filterAll") : t(`status.${f}`);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div role="group" aria-label={t("po.colStatus")} className="flex flex-wrap gap-2">
        {filters.map((f) => {
          const active = search.status === f;
          return (
            <button
              key={f}
              type="button"
              aria-pressed={active}
              onClick={() => setSearch({ status: f, po: undefined })}
              className={cn(
                "h-8 rounded-full border px-3 text-[13px] transition-colors",
                active
                  ? "border-ink bg-ink font-medium text-surface"
                  : "border-border bg-surface text-ink hover:bg-surface-hover",
              )}
            >
              {label(f)}
            </button>
          );
        })}
      </div>
      <div className="grow" />
      <label className="flex h-9 min-w-[240px] items-center gap-2 rounded-xl border border-border bg-surface px-3 text-ink-muted focus-within:outline-2 focus-within:outline-focus">
        <Search size={15} aria-hidden="true" />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("po.searchPlaceholder")}
          aria-label={t("po.searchPlaceholder")}
          data-list-search
          className="min-w-0 grow bg-transparent text-[13px] text-ink outline-none placeholder:text-ink-muted"
        />
        {text && (
          <button type="button" aria-label={t("po.clearFilters")} onClick={() => setText("")} className="text-ink-muted">
            <X size={14} />
          </button>
        )}
      </label>
    </div>
  );
}

function OrderList({
  search,
  setSearch,
  tools,
  onPick,
}: {
  search: Search;
  setSearch: (p: Partial<Search>) => void;
  tools: ListTools<PurchaseOrderRow>;
  /** A row was opened: the mass panel gives way to the order. */
  onPick: () => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const queryClient = useQueryClient();
  const today = todayIso();
  const { company } = useActiveCompany();
  const params = listParams(company?.id ?? "", search);
  const query = useInfiniteQuery(purchaseOrderListQuery(params));
  const view: ListView = {
    spec: purchaseOrderListColumns,
    query: { filters: params.filters, sort: params.sort },
    onChange: (next) => setSearch({ f: encodeFilters(next.filters) || undefined, s: encodeSort(next.sort) || undefined }),
  };

  const isLate = (r: PurchaseOrderRow) => isOpenStatus(r.status) && !!r.next_promised_date && r.next_promised_date < today;

  const columns: Column<PurchaseOrderRow>[] = [
    {
      key: "number",
      header: t("po.colOrder"),
      width: "minmax(132px,0.9fr)",
      cell: (r, selected) => (
        <span className={cn("font-mono text-xs", selected && "font-semibold text-accent-ink")}>{r.number}</span>
      ),
    },
    {
      key: "date",
      header: t("po.orderDate"),
      width: "minmax(112px,0.8fr)",
      cell: (r) => <span className="text-ink-muted">{fmt.day(r.order_date)}</span>,
    },
    {
      key: "supplier",
      header: t("po.colSupplier"),
      width: "minmax(150px,1.4fr)",
      cell: (r, selected) => <span className={cn("truncate", selected && "font-medium")}>{r.supplier_name}</span>,
    },
    {
      key: "status",
      header: t("po.colStatus"),
      width: "minmax(150px,1fr)",
      cell: (r) => <Badge tone={statusTone[r.status]}>{t(`status.${r.status}`)}</Badge>,
      options: purchaseOrderStatuses.map((v) => ({ value: v, label: t(`status.${v}`) })),
    },
    {
      key: "delivery",
      header: t("po.colDelivery"),
      width: "minmax(124px,0.9fr)",
      cell: (r) => {
        const late = isLate(r);
        return (
          <span className={cn("flex items-center gap-1.5", late ? "font-medium text-(--status-danger-fg)" : "text-ink-muted")}>
            {r.next_promised_date ? fmt.day(r.next_promised_date) : "—"}
            {late && <Clock size={13} strokeWidth={2.2} aria-label={t("po.late")} role="img" />}
          </span>
        );
      },
    },
    {
      key: "amount",
      header: t("po.colAmount"),
      width: "minmax(140px,1fr)",
      align: "end",
      cell: (r) => <span className="tabular">{fmt.money(String(r.total_amount), r.currency)}</span>,
    },
  ];

  return (
    <DataList
      label={t("po.listLabel")}
      columns={columns}
      query={query}
      rowId={(r) => r.id}
      selectedId={search.po}
      onSelect={(r) => {
        onPick();
        setSearch({ po: r.id, copy: undefined });
      }}
      onClear={() => setSearch({ po: undefined })}
      onPrefetch={(r) => void queryClient.prefetchQuery(purchaseOrderDetailQuery(r.id))}
      emptyTitle={t("po.emptyTitle")}
      emptyBody={t("po.emptyBody")}
      emptyAction={
        <button
          type="button"
          onClick={() => setSearch({ status: "open", q: "", po: undefined, f: undefined, s: undefined })}
          className="h-9 rounded-xl border border-border px-3.5 text-[13px] font-medium hover:bg-surface-hover"
        >
          {t("list.clearFilters")}
        </button>
      }
      minWidth={820}
      tools={tools}
      view={view}
    />
  );
}
